import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, cn } from "@paribus/shared-ui";
import { X } from "lucide-react";
import React from "react";

type IconComponent = React.ComponentType<{ size?: number | string; className?: string }>;

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  isConfirming?: boolean;
  variant?: "default" | "destructive";
  icon?: IconComponent;
  confirmIcon?: IconComponent;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  isConfirming = false,
  variant = "default",
  icon: Icon,
  confirmIcon: ConfirmIcon,
}) => {
  const handleOpenChange = (nextOpen: boolean) => {
    if (isConfirming) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md gap-0 rounded-2xl p-6"
        hideCloseButton
        onMouseDown={(e) => e.stopPropagation()}
        overlayProps={{
          className: "bg-black/60 backdrop-blur-sm",
          onMouseDown: (e) => e.stopPropagation(),
        }}
        onInteractOutside={(e) => {
          if (isConfirming) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isConfirming) e.preventDefault();
        }}
      >
        <DialogClose
          type="button"
          disabled={isConfirming}
          aria-label="Close dialog"
          className="absolute right-6 top-6 rounded-full p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          <X size={20} />
        </DialogClose>
        {Icon && (
          <div
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-xl shadow-sm",
              variant === "destructive"
                ? "bg-destructive/10 text-destructive"
                : "bg-primary-subtle text-primary"
            )}
          >
            <Icon size={24} />
          </div>
        )}
        <DialogHeader className={cn("space-y-3 text-left", Icon ? "mt-4" : "pr-10")}>
          <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
          <DialogDescription asChild className="leading-relaxed">
            <div>{description}</div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-8 gap-3 sm:space-x-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isConfirming}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === "destructive" ? "destructive" : "primary"}
            onClick={() => {
              void onConfirm();
            }}
            loading={isConfirming}
            leadingIcon={ConfirmIcon ? <ConfirmIcon size={16} /> : undefined}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
